/**
 * Responsive image utilities for CDN-served images.
 *
 * Convention: when an image is uploaded via the API, the backend generates
 * WebP variants alongside the original at:
 *   {stem}-400w.webp
 *   {stem}-800w.webp
 *   {stem}-1200w.webp
 *
 * These helpers derive those variant URLs from any CDN URL and build a
 * proper srcset string for use in <picture> / <img srcset>.
 */

const VARIANT_WIDTHS = [400, 800, 1200]

/**
 * Given a CDN URL, returns an array of { url, width } objects for each
 * responsive variant that the upload pipeline generates.
 *
 * Works with any path structure — only the filename stem matters.
 *
 * @example
 * getImageVariants('https://cdn.tlffs.ca/clients/org123/logo.jpg')
 * // => [
 * //   { url: 'https://cdn.tlffs.ca/clients/org123/logo-400w.webp',  width: 400 },
 * //   { url: 'https://cdn.tlffs.ca/clients/org123/logo-800w.webp',  width: 800 },
 * //   { url: 'https://cdn.tlffs.ca/clients/org123/logo-1200w.webp', width: 1200 },
 * // ]
 */
export function getImageVariants(url) {
  if (!url) return []

  try {
    const parsed = new URL(url)
    const pathParts = parsed.pathname.split('/')
    const originalFilename = pathParts[pathParts.length - 1]
    const stem = originalFilename.replace(/\.[^.]+$/, '') // strip extension
    const basePath = parsed.origin + pathParts.slice(0, -1).join('/')

    return VARIANT_WIDTHS.map(w => ({
      url: `${basePath}/${stem}-${w}w.webp`,
      width: w,
    }))
  } catch {
    return []
  }
}

/**
 * Builds a srcset string for use on <img srcset> or <source srcset>.
 *
 * @example
 * buildSrcSet('https://cdn.tlffs.ca/clients/org123/logo.jpg')
 * // => 'https://cdn.tlffs.ca/clients/org123/logo-400w.webp 400w, ...'
 */
export function buildSrcSet(url) {
  return getImageVariants(url)
    .map(v => `${v.url} ${v.width}w`)
    .join(', ')
}

/**
 * Returns true if the URL is for a CDN-hosted image that has responsive
 * variants (i.e., it was uploaded through the API pipeline, not an SVG,
 * external URL, or placeholder).
 */
export function hasResponsiveVariants(url) {
  if (!url) return false
  const lower = url.toLowerCase()
  // SVGs don't need rasterised variants; external URLs have unknown structure
  if (lower.endsWith('.svg')) return false
  // Only generate srcset for URLs from our own CDN
  if (!lower.includes('cdn.tlffs.ca') && !lower.includes('tlffs.ca/clients')) return false
  // Old uploads have no file extension — their WebP variants were never generated,
  // so trying to build a srcset would produce 404 URLs that silently break the image
  // (browsers do not fall back to <img> when a matched <source> returns a network error).
  const pathname = url.split('?')[0]
  const filename = pathname.split('/').pop()
  const hasExtension = filename.includes('.')
  return hasExtension
}
